"use client";
import { useLayoutEffect, useState } from "react";
import dynamic from "next/dynamic";
import { JapanArea } from "@/types/generated";
import type { Attractions as LocationMapViewAttractions } from "../hotel-details/location-section/LocationMapView.types";
import { mapAreasToDisplayData } from "./utils";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import { MapBrandIcon } from "@app/[locale]/tourist-attraction/[city_slug]/[slug]/util";
import { cn } from "@/lib/utils";
import { GOOGLE_MAPS_CONFIG } from "@/configs/config";

// Lazy load LocationMapView to avoid bundling @vis.gl/react-google-maps in initial JS
const LocationMapView = dynamic(
  () => import("../hotel-details/location-section/LocationMapView"),
  {
    ssr: false,
    loading: () => (
      <div className="h-full w-full animate-pulse rounded-lg bg-gray-200" />
    ),
  },
);

export type MapCenter = {
  lat: number;
  lng: number;
  zoom: number;
};

interface DestinationMapProps {
  japanAreas: JapanArea[];
  defaultZoom?: boolean;
  brandIcons?: MapBrandIcon[];
  mapClassName?: string;
}
/**
 * DestinationMap Component
 * Renders an interactive map displaying hotel or attraction areas across Japan.
 * - Transforms area data into a format compatible with `LocationMapView`.
 * - Uses `useLayoutEffect` + `requestAnimationFrame` to ensure smooth state updates.
 * - Displays a fallback if Google Maps API key is missing.
 */
export const DestinationMap = ({
  japanAreas,
  defaultZoom,
  brandIcons,
  mapClassName,
}: DestinationMapProps) => {
  const baseLat = 38.1895;
  const baseLng = 137.1917;

  // Default map center — roughly central Japan
  const defaultCenter: MapCenter = {
    lat: baseLat,
    lng: baseLng,
    zoom: 4.5,
  };

  const [transformedData, setTransformedData] = useState<
    LocationMapViewAttractions[]
  >([]);
  const { japanAreas: japanAreasStore } = useJapanAreasStore();

  // Ensures data transformation happens synchronously before paint,
  // reducing flicker when switching areas or re-rendering the map
  useLayoutEffect(() => {
    const frameId = requestAnimationFrame(() => {
      if (japanAreas && japanAreas.length > 0) {
        const mappedData = mapAreasToDisplayData(japanAreas, japanAreasStore);
        setTransformedData(mappedData);
      } else {
        setTransformedData([]);
      }
    });

    // Cleanup on unmount or dependency change
    return () => cancelAnimationFrame(frameId);
  }, [japanAreas, japanAreasStore]);

  // Fallback in case the API key is not set in environment
  if (!GOOGLE_MAPS_CONFIG.apiKey ) {
    return <div>Google Maps API Key is missing.</div>;
  }

  return (
    <div className="flex h-full w-full flex-col gap-8 text-center">
      <div
        className={cn(
          "relative h-[600px] w-full overflow-hidden lg:rounded-2xl",
          mapClassName,
        )}
      >
        <LocationMapView
          data={transformedData}
          center={defaultCenter}
          defaultZoom={defaultZoom}
          brandIcons={brandIcons}
        />
      </div>
    </div>
  );
};
